import React, { Component } from 'react';
import Icon from '../../../icons/Icon';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import VariableType from '../../../constant/VariableType';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import { EnrichmentBase } from '../../../domain/enrichment/EnrichmentDomain';
import _ from 'lodash';
import ExpressionComponent from './ExpressionComponent';
import { Syntax, TokenVariable, VariableToken } from '@trlogic/ui-pack';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import ExpressionBridge from '../../../util/ExpressionBridge';
import ReadOnlyQueryableRepository = repository.ReadOnlyQueryableRepository;
import eq = query.eq;
import SubscriptionContext = subscribe.SubscriptionContext;
import QueryBuilder = query.QueryBuilder;
import { MLOpsModel } from '../../../domain/mlops/MLOpsDomains';
import DataType from '../../../constant/DataType';
import LanguageUtil from '../../../util/LanguageUtil';
import { FormicaProps, withFormica } from 'helpers/withFormica';
import { RuleTypes } from 'domain/collection/rule/RuleDomains';

class ExpressionBuilderComponent extends Component<Props, State> {
  public state: State = new State();
  private subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);
  private computation: subscribe.Computation | undefined;

  private readonly repository: ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  public render() {
    const { expression, disabled, syntaxUpdated } = this.props;
    const { loading, variables, variableUpdated } = this.state;
    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'flex flex-col h-full font-formica border-[1px] gap-y-4 border-[#E0E4E8] rounded-lg overflow-scroll'}>
        <div className={'flex justify-start gap-x-1 items-center w-full min-h-[48px] border-b-[1px] border-[#E0E4E8] px-5 bg-main-gray rounded-t-lg'}>
          <Icon icon={'code'} size={20} color={'#284564'} />
          <span className={'text-primary text-lg font-bold'}>{LanguageUtil.get({ id: 'common.label.condition', defaultMessage: 'Condition' })}</span>
        </div>
        <div className={'w-full overflow-scroll p-2'}>
          <ExpressionComponent
            variables={variables}
            syntax={expression}
            onChangeSyntax={this.onChangeSyntax}
            disabled={disabled}
            variableUpdated={variableUpdated}
            syntaxUpdated={syntaxUpdated}
          />
        </div>
      </div>
    );
  }

  public componentDidMount(): void {
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const enrichment = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS);
    this.computation = subscribe.Tracker.autorun(this.tracker, [...enrichment]);
  };

  private tracker = async () => {
    if (this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.ENRICHMENTS)?.some((e) => e.ready())) {
      const variables: any = await this.getVariables();
      if (variables) {
        await this.expressionBridge.start(this.props.expression, variables);
        this.setState({ loading: false, variables, variableUpdated: this.state.variableUpdated + 1 });
      }
    }
  };

  private stopTracker = async () => {
    this.computation?.stop();
  };

  private getTransactionName = () => {
    const { transaction } = this.props;
    return transaction;
  };

  private getVariables = async () => {
    const { ruleType } = this.props;
    const eventName = this.getTransactionName();
    const eventQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', eventName)).build();
    const event = await this.repository.find<TransactionJsonModel>(eventQuery);
    if (event == undefined) return {};
    const eventVariables: any = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE,
        name: v.name,
        properties: v.properties,
        args: {
          event: eventName,
        },
      },
    }));
    const previous: any = event.variables.map((v) => ({
      type: VariableType.PREVIOUS_EVENT_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.PREVIOUS_EVENT_VARIABLE,
        name: v.name,
        properties: v.properties,
        args: {
          event: eventName,
        },
      },
    }));
    const enrichmentVariables: Record<
      Exclude<VariableType, VariableType.EVENT_VARIABLE | VariableType.ACTOR_VARIABLE | VariableType.PREVIOUS_EVENT_VARIABLE>,
      TokenVariable<VariableToken>[]
    > = _.groupBy(
      ((await this.repository.findMany(query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).build())) as EnrichmentBase[]).map((e) => ({
        type: e.type,
        variable: {
          type: 'Common Variable',
          variableType: e.type,
          name: e.name,
          properties: e.properties,
          args: {},
        },
      })),
      (e: any) => e.type,
    ) as Record<any, any[]>;
    const mlopsVariables: Record<string, TokenVariable<VariableToken>[]> = (
      await this.repository.findMany<MLOpsModel>(QueryBuilder.select().from(CollectionNames.AI_MODELS).build())
    ).map((e) => ({
      type: VariableType.ML_OPS_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.ML_OPS_VARIABLE,
        name: e.name,
        properties: { dataType: DataType.NUMBER },
        args: {},
      },
    })) as any;
    const resolutions = [
      {
        name: 'reason',
        properties: {
          dataType: DataType.STRING,
        },
        description: '',
      },
      {
        name: 'score',
        properties: {
          dataType: DataType.NUMBER,
        },
        description: '',
      },
      {
        name: 'rules',
        properties: {
          dataType: DataType.ARRAY,
          properties: {
            dataType: DataType.OBJECT,
            variables: [
              {
                name: 'name',
                description: '',
                properties: {
                  dataType: DataType.STRING,
                },
              },
              {
                name: 'reason',
                description: '',
                properties: {
                  dataType: DataType.STRING,
                },
              },
              {
                name: 'score',
                description: '',
                properties: {
                  dataType: DataType.NUMBER,
                },
              },
              // {
              //   name: 'attachment',
              //   description: '',
              //   properties: {
              //     dataType: DataType.OBJECT,
              //     variables: [],
              //   },
              // },
            ],
          },
        },
        description: '',
      },
    ];
    const resolutionVariables = resolutions.map((r) => ({
      type: VariableType.RESOLUTION_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.RESOLUTION_VARIABLE,
        name: r.name,
        properties: r.properties,
        args: {
          event: 'Resolution',
        },
      },
    }));
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      [VariableType.PREVIOUS_EVENT_VARIABLE]: previous,
      ...enrichmentVariables,
      ...(ruleType === RuleTypes.RESOLUTION ? { [VariableType.RESOLUTION_VARIABLE]: resolutionVariables } : {}),
      [VariableType.ML_OPS_VARIABLE]: mlopsVariables,
    };
  };

  private onChangeSyntax = (syntax: Syntax) => {
    const { onChange } = this.props;
    onChange(syntax);
  };
}

interface Props extends FormicaProps {
  transaction?: string;
  expression: Syntax;
  onChange: (expression: Syntax) => void;
  syntaxUpdated: number;
  close: () => void;
  disabled?: boolean;
  ruleType: RuleTypes;
}

class State {
  loading: boolean = true;
  variables: Record<string, TokenVariable[]> = {};
  variableUpdated: number = 0;
}

export default withFormica(ExpressionBuilderComponent);
